import React, { useEffect, useRef, useState } from "react";
import {
  TextField,
  Button,
  Grid,
  Typography,
  IconButton,
  CardMedia,
} from "@mui/material";
import styles from "./RegisterAdmin.module.css";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Dropdown from "../../Dropdown/Dropdown";
import { useToastContext } from "../../../../Context/ToastContext";
import { ClearIcon } from "@mui/x-date-pickers";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useUserContext } from "../../../../Context/UserContext";
import { Helmet } from "react-helmet";
import { Box } from "@mui/system";
import { apiGet, apiPost } from "../../../../utils/ApiManager";

const registerAdmin = process.env.REACT_APP_REGISTER_ADMIN;
const fetchDropdownRegisterAdmin =
  process.env.REACT_APP_DROPDOWN_REGISTER_ADMIN;

const RegisterAdmin = () => {
  const { requestConfirmation } = useUserContext();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNo: "",
    department: "",
    role: "",
    employeeId: "",
    password: "",
    confirmPassword: "",
    profileImage: null,
  });
  const [departments, setDepartments] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);

  const { showToast } = useToastContext();
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      setLoading(true);
      await fetchDropdowns();
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleClick = () => {
    fileInputRef.current.value = null;
  };

  useEffect(() => {
    fetchData(); // on component mounting thiswill load and display the data
  }, []);

  const fetchDropdowns = async () => {
    try {
      const response = await apiGet(fetchDropdownRegisterAdmin);
      if (response.success) {
        setDepartments(response.data?.departmentList || []);
        setRoles(response.data?.roleList || []);
      } else {
        showToast(response?.data?.message || "Something went wrong !", "error");
      }
    } catch (error) {
      showToast(
        error?.response?.message || error?.message || "Something went wrong !",
        "error"
      );
    }
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    // Reset errors for the changed field
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    const fileSizeInKB = file.size / 1024;
    const allowedFileTypes = ["image/jpeg", "image/jpg", "image/png"];

    if (fileSizeInKB > 300) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        profileImage: "File size must be less than 300 KB.",
      }));
      return;
    }

    if (!allowedFileTypes.includes(file.type)) {
      // showToast("Only image files (JPEG, PNG) are allowed.", "error");
      setErrors((prevErrors) => ({
        ...prevErrors,
        profileImage: "Only image files (JPEG, PNG) are allowed.",
      }));
      return;
    }

    setFormData((prevData) => ({
      ...prevData,
      profileImage: file,
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      profileImage: "",
    }));
  };

  const handleClearFile = (fieldName) => {
    setFormData((prevData) => ({
      ...prevData,
      [fieldName]: null,
      [`${fieldName}Link`]: null,
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // First Name
    if (!formData.firstName) {
      newErrors.firstName = "First name is required";
      isValid = false;
    }

    // Last Name
    if (!formData.lastName) {
      newErrors.lastName = "Last name is required";
      isValid = false;
    }

    // Employee Id
    if (!formData.employeeId) {
      newErrors.employeeId = "Employee ID is required";
      isValid = false;
    } else if (formData.employeeId.length > 20) {
      newErrors.employeeId = "Employee ID should be less than 20 characters";
      isValid = false;
    }

    // Email
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!formData.email) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    // Phone Number
    const phoneRegex = /^[0-9]{10}$/;
    if (!formData.phoneNo) {
      newErrors.phoneNo = "Phone number is required";
      isValid = false;
    } else if (!phoneRegex.test(formData.phoneNo)) {
      newErrors.phoneNo = "Phone number must be exactly 10 digits";
      isValid = false;
    }

    // Department
    if (!formData.department) {
      newErrors.department = "Please select a department";
      isValid = false;
    }

    // Role
    if (!formData.role) {
      newErrors.role = "Please select a job level";
      isValid = false;
    }

    // Password
    const passwordRegex =
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;
    if (!formData.password) {
      newErrors.password = "Password is required";
      isValid = false;
    } else if (!passwordRegex.test(formData.password)) {
      newErrors.password =
        "Password must start with a capital letter, contain alphanumeric characters and at least one special character";
      isValid = false;
    }

    // Confirm Password
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Confirm password is required";
      isValid = false;
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  useEffect(() => {
    const isValid = validateForm(true);
    setIsFormValid(isValid);
  }, [formData]);

  const handleBackClick = () => {
    navigate(-1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent form default first

    const isValid = validateForm();
    if (!isValid) return;

    const result = await requestConfirmation("Want to register new admin ?");
    if (!result) return;
    try {
      setLoading(true);
      const formDataToSend = new FormData();

      Object.keys(formData).forEach((key) => {
        if (formData[key]) {
          formDataToSend.append(key, formData[key]);
        }
      });
      const response = await apiPost(registerAdmin, formDataToSend, true);
      if (response.success) {
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phoneNo: "",
          department: "",
          employeeId: "",
          role: "",
          password: "",
          confirmPassword: "",
          profileImage: null,
        });
        navigate(-1);
        showToast(
          response.message || "Admin profile created successfully.",
          "success"
        );
      } else {
        showToast(response.message || "Failed to register admin", "error");
      }
    } catch (error) {
      showToast(
        error.response.message || "Failed to create admin profile.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.mainContainer}>
      <Helmet>
        <title>Register Admin | DiNEST</title>
        <meta name="description" content="register admin on DiNEST" />
      </Helmet>
      <div onClick={handleBackClick} className={styles.backIconContainer}>
        <ArrowBackIcon className={styles.backIcon} />
      </div>
      <Typography variant="h5" className={styles.title}>
        Admin Registration
      </Typography>
      <form onSubmit={handleSubmit} className={styles.formScrollable}>
        <Grid container spacing={2}>
          {/* First Name */}
          <Grid size={{ xs: 12 }} className={styles.inputGroup}>
            <TextField
              label="First Name"
              type="text"
              variant="standard"
              fullWidth
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
              className={styles.inputTyper}
            />
            {errors.firstName && (
              <p className={styles.error}>{errors.firstName}</p>
            )}
          </Grid>

          {/* Last Name */}
          <Grid size={{ xs: 12 }} className={styles.inputGroup}>
            <TextField
              label="Last Name"
              type="text"
              variant="standard"
              fullWidth
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
            />
            {errors.lastName && (
              <p className={styles.error}>{errors.lastName}</p>
            )}
          </Grid>

          {/* Email */}
          <Grid size={{ xs: 12 }} className={styles.inputGroup}>
            <TextField
              label="Email"
              type="email"
              variant="standard"
              fullWidth
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
            {errors.email && <p className={styles.error}>{errors.email}</p>}
          </Grid>

          {/* Phone Number */}
          <Grid size={{ xs: 12 }} className={styles.inputGroupDropdown}>
            <TextField
              label="Phone Number"
              type="number"
              variant="standard"
              fullWidth
              name="phoneNo"
              value={formData.phoneNo}
              onChange={handleChange}
              required
              InputProps={{
                inputProps: { inputMode: "numeric", pattern: "[0-9]*" },
                classes: { input: styles.noArrows },
              }}
            />
            {errors.phoneNo && <p className={styles.error}>{errors.phoneNo}</p>}
          </Grid>

          <Grid size={{ xs: 12 }} className={styles.inputGroup}>
            <TextField
              label="Employee Id"
              type="text"
              variant="standard"
              fullWidth
              name="employeeId"
              value={formData.employeeId}
              onChange={handleChange}
              required
              sx={{
                "& .MuiInputBase-input": {
                  fontFamily: "Fonarto, sans-serif",
                },
                "& .MuiInputLabel-root": {
                  fontFamily: "Fonarto, sans-serif",
                },
              }}
            />
            {errors.employeeId && (
              <p className={styles.error}>{errors.employeeId}</p>
            )}
          </Grid>

          {/* Department */}
          <Grid size={{ xs: 12 }} className={styles.inputGroup}>
            <Dropdown
              label="Department"
              fullWidth
              name="department"
              value={formData.department}
              onChange={handleChange}
              options={departments}
              required
            />
            {errors.department && (
              <p className={styles.error}>{errors.department}</p>
            )}
          </Grid>

          {/* Job Title */}
          <Grid size={{ xs: 12 }} className={styles.inputGroup}>
            <Dropdown
              label="Job Level"
              // variant="outlined"
              fullWidth
              name="role"
              value={formData.role}
              onChange={handleChange}
              required
              options={roles}
            />
            {errors.role && <p className={styles.error}>{errors.role}</p>}
          </Grid>

          <Grid size={{ xs: 12 }} className={styles.inputGroup}>
            <TextField
              label="Password"
              type="password"
              variant="standard"
              fullWidth
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
            />
            {errors.password && (
              <p className={styles.error}>{errors.password}</p>
            )}
          </Grid>

          <Grid size={{ xs: 12 }} className={styles.inputGroup}>
            <TextField
              label="Confirm Password"
              type="password"
              variant="standard"
              fullWidth
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
            />
            {errors.confirmPassword && (
              <p className={styles.error}>{errors.confirmPassword}</p>
            )}
          </Grid>

          {/* Profile Image */}
          <Grid size={{ xs: 12 }} className={styles.profileImageInput}>
            <Box>
              <Typography variant="body1" className={styles.title}>
                Upload profile image
              </Typography>
              <Typography variant="caption" color="text.secondary">
                (Image &lt; 300kb)
              </Typography>
            </Box>
            {/* Upload Button */}
            <Button
              variant="outlined"
              component="label"
              fullWidth
              onClick={handleClick}
              className={styles.uploadButton}
              startIcon={<CloudUploadIcon />}
            >
              Upload
              <input
                type="file"
                hidden
                name="profileImage"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
              />
            </Button>
            {errors.profileImage && (
              <p className={styles.errorProfileImage}>{errors.profileImage}</p>
            )}

            {/* File name and Clear button */}
            {formData.profileImage && (
              <Box display="flex" justifyContent="center">
                <Box display="flex" alignItems="center">
                  <Typography
                    variant="caption"
                    sx={{
                      display: "inline-block",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      maxWidth: 120,
                      cursor: "pointer",
                    }}
                  >
                    {formData.profileImage.name}
                  </Typography>
                  <IconButton
                    onClick={() => handleClearFile("profileImage")}
                    sx={{ ml: 0.5 }}
                  >
                    <ClearIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Box>
              </Box>
            )}

            {/* Image preview */}
            {formData.profileImage && (
              <CardMedia
                component="img"
                height="150"
                image={URL.createObjectURL(formData.profileImage)}
              />
            )}
          </Grid>
        </Grid>

        {/* Submit Button */}
        <Grid className={styles.buttonContainer}>
          <Button
            disabled={!isFormValid}
            type="submit"
            variant="contained"
            fullWidth
          >
            Save
          </Button>
        </Grid>
      </form>
    </div>
  );
};

export default RegisterAdmin;
